import * as React from 'react'
import {
  Play,
  Loader2,
  Clock,
  Info,
  TrendingUp,
  ArrowRight,
  CheckCircle2,
  XCircle,
  X,
  FileText,
  Sliders,
  ChevronDown,
  Shield,
  Activity,
  Zap,
  AlertCircle,
  RotateCcw,
} from 'lucide-react'
import { DataGenerator, type DataGeneratorMeta } from '@/components/DataGenerator'
import type { InputFormat } from '@/types'
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { usePersistStore, defaultGaussianParams } from '@/stores/persistStore'
import { FileUploader } from '@/components/FileUploader'
import { ImportDataBanner } from '@/components/ImportDataBanner'
import { ExportButton } from '@/components/ExportButton'
import { ResultSummaryBar } from '@/components/ResultSummaryBar'
import { SnapshotManager } from '@/components/SnapshotManager'
import { ChartExportButton } from '@/components/ChartExportButton'
import { DataExportMenu } from '@/components/DataExportMenu'
import { SectionHeader } from '@/components/SectionHeader'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import {
  DEFAULT_GAUSSIAN_PARAMS,
  GAUSSIAN_TEST_NAMES,
  MIN_SAMPLES_GAUSSIAN,
  APP_VERSION,
} from '@/lib/constants'
import type {
  GaussianSuiteResult,
  GaussianParameters,
  GaussianTestResult,
  ShapiroWilkResult,
  AndersonDarlingResult,
  DAgostinoPearsonResult,
  KSTestResult,
  ExportReport,
  SP80022SuiteResult,
  SP80022TestResult,
  SP80090BResult,
  IIDStatisticResult,
  EntropyEstimateResult,
} from '@/types'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(ms: number): string {
  if (ms < 1000) return `${ms.toFixed(0)}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  const mins = Math.floor(ms / 60_000)
  const secs = ((ms % 60_000) / 1000).toFixed(0)
  return `${mins}m ${secs}s`
}

function formatPValue(p: number): string {
  if (p < 0.0001) return p.toExponential(3)
  return p.toFixed(6)
}

function pValueColor(p: number): string {
  if (p >= 0.01) return 'text-green-600 dark:text-green-400'
  if (p >= 0.001) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

function sp22PValueColor(p: number, alpha: number): string {
  if (p >= alpha) return 'text-green-600 dark:text-green-400'
  if (p >= alpha / 10) return 'text-yellow-600 dark:text-yellow-400'
  return 'text-red-600 dark:text-red-400'
}

function sp22PValueBgColor(p: number, alpha: number): string {
  if (p >= alpha) return 'bg-green-500/10'
  if (p >= alpha / 10) return 'bg-yellow-500/10'
  return 'bg-red-500/10'
}

function formatEntropy(v: number): string {
  if (!isFinite(v) || isNaN(v)) return '---'
  return v.toFixed(6)
}

function getTestStatistic(test: GaussianTestResult): { label: string; value: number } {
  switch (test.testName) {
    case 'Shapiro-Wilk':
      return { label: 'W', value: (test as ShapiroWilkResult).W }
    case 'Anderson-Darling':
      return { label: 'A\u00B2', value: (test as AndersonDarlingResult).A2 }
    case "D'Agostino-Pearson":
      return { label: 'K\u00B2', value: (test as DAgostinoPearsonResult).K2 }
    case 'Kolmogorov-Smirnov':
      return { label: 'D', value: (test as KSTestResult).D }
    default:
      return { label: '?', value: 0 }
  }
}

// Subsample Q-Q plot data for rendering performance
function subsampleQQ(
  data: Array<{ theoretical: number; observed: number }>,
  maxPoints: number,
): Array<{ theoretical: number; observed: number }> {
  if (data.length <= maxPoints) return data
  const step = data.length / maxPoints
  const result: Array<{ theoretical: number; observed: number }> = []
  for (let i = 0; i < maxPoints; i++) {
    result.push(data[Math.floor(i * step)]!)
  }
  // Always include the last point
  result.push(data[data.length - 1]!)
  return result
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GaussianPage() {
  const persist = usePersistStore()
  const {
    gaussianParams: persistedParams,
    gaussianResult: persistedResult,
    setGaussianParams: persistParams,
    setGaussianResult: persistResult,
    clearGaussianResult,
  } = persist

  // Per-module input data (persisted)
  const [inputData, setInputDataLocal] = React.useState<import('@/lib/fileParser').ParseResult | null>(
    () => persist.getModuleInput('gaussian')
  )
  const setInputData = React.useCallback((data: import('@/lib/fileParser').ParseResult) => {
    setInputDataLocal(data)
    persist.setModuleInput('gaussian', data)
  }, [persist])

  // Parameters (initialize from persist store, fallback to defaults)
  const [useEstimated, setUseEstimated] = React.useState<boolean>(
    persistedParams?.useEstimated ?? DEFAULT_GAUSSIAN_PARAMS.useEstimated,
  )
  const [knownMu, setKnownMu] = React.useState<string>(
    persistedParams?.mu != null ? String(persistedParams.mu) : '0',
  )
  const [knownSigma, setKnownSigma] = React.useState<string>(
    persistedParams?.sigma != null ? String(persistedParams.sigma) : '1',
  )
  const [alpha, setAlpha] = React.useState<number>(
    persistedParams?.alpha ?? DEFAULT_GAUSSIAN_PARAMS.alpha,
  )

  // Execution state
  const [isRunning, setIsRunning] = React.useState(false)
  const [progressPct, setProgressPct] = React.useState(0)
  const [completedTests, setCompletedTests] = React.useState<string[]>([])
  const [localResult, setLocalResult] = React.useState<GaussianSuiteResult | null>(null)
  const result = localResult ?? persistedResult ?? null
  const setResult = React.useCallback((r: GaussianSuiteResult | null) => {
    setLocalResult(r)
    if (r) persistResult(r)
    else clearGaussianResult()
  }, [persistResult, clearGaussianResult])
  const [error, setError] = React.useState<string | null>(null)

  // Also hydrate PIT results
  React.useEffect(() => {
    if (persist.pitSP22Result && !sp22Result) setSp22Result(persist.pitSP22Result)
  }, [persist.pitSP22Result])
  React.useEffect(() => {
    if (persist.pitSP90BResult && !sp90bResult) setSp90bResult(persist.pitSP90BResult)
  }, [persist.pitSP90BResult])

  // Sync params to persist store
  React.useEffect(() => {
    persistParams({
      useEstimated,
      alpha,
      ...(useEstimated ? {} : { mu: parseFloat(knownMu) || 0, sigma: parseFloat(knownSigma) || 1 }),
    })
  }, [useEstimated, alpha, knownMu, knownSigma, persistParams])

  // Restore defaults handler
  const restoreDefaults = React.useCallback(() => {
    setUseEstimated(defaultGaussianParams.useEstimated)
    setAlpha(defaultGaussianParams.alpha)
    setKnownMu('0')
    setKnownSigma('1')
  }, [])

  // Close results handler
  const handleCloseResults = React.useCallback(() => {
    setResult(null)
    clearGaussianResult()
  }, [clearGaussianResult])

  // PIT state (persisted)
  const [pitQuantization, setPitQuantizationLocal] = React.useState<'1bit' | '8bit' | '32bit'>(persist.pitQuantization)
  const [isPitRunning, setIsPitRunning] = React.useState(false)
  const [pitResult, setPitResultLocal] = React.useState<{ data: Uint8Array | Uint32Array; bitLength: number } | null>(
    () => persist.getPitData()
  )

  const setPitQuantization = React.useCallback((q: '1bit' | '8bit' | '32bit') => {
    setPitQuantizationLocal(q)
    persist.setPitQuantization(q) // also clears PIT data & results
    setPitResultLocal(null)
    setSp22Result(null)
    setSp90bResult(null)
  }, [persist])

  const setPitResult = React.useCallback((r: { data: Uint8Array | Uint32Array; bitLength: number } | null) => {
    setPitResultLocal(r)
    if (r) persist.setPitData(r.data, r.bitLength)
    else persist.clearPitData()
  }, [persist])

  // Inline SP 800-22 state (persisted)
  const [sp22Running, setSp22Running] = React.useState(false)
  const [sp22Progress, setSp22Progress] = React.useState(0)
  const [sp22CompletedTests, setSp22CompletedTests] = React.useState<string[]>([])
  const [sp22Result, setSp22Result] = React.useState<SP80022SuiteResult | null>(
    persist.pitSP22Result ?? null
  )

  // Inline SP 800-90B state (persisted)
  const [sp90bRunning, setSp90bRunning] = React.useState(false)
  const [sp90bProgress, setSp90bProgress] = React.useState(0)
  const [sp90bProgressMsg, setSp90bProgressMsg] = React.useState('')
  const [sp90bResult, setSp90bResult] = React.useState<SP80090BResult | null>(
    persist.pitSP90BResult ?? null
  )

  const workerRef = React.useRef<Worker | null>(null)
  const sp22WorkerRef = React.useRef<Worker | null>(null)
  const sp90bWorkerRef = React.useRef<Worker | null>(null)

  // Handle generated data from DataGenerator
  const handleGeneratedData = React.useCallback(
    (data: Uint8Array | Float64Array, meta: DataGeneratorMeta) => {
      if (!(data instanceof Float64Array)) return
      setInputData({
        kind: 'continuous',
        input: {
          data,
          sampleCount: meta.sampleCount,
          fileName: `generated-${meta.description.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`,
          fileSize: data.byteLength,
          format: 'float-csv' as InputFormat,
        },
      })
      // Pre-fill mu/sigma if known Gaussian parameters were used
      const muParam = meta.parameters['mu']
      const sigmaParam = meta.parameters['sigma']
      if (typeof muParam === 'number' && typeof sigmaParam === 'number') {
        setKnownMu(String(muParam))
        setKnownSigma(String(sigmaParam))
      }
      setResult(null)
      setPitResult(null)
      setError(null)
    },
    [setInputData],
  )

  // Check if continuous data is loaded
  const continuousData = React.useMemo<Float64Array | null>(() => {
    if (!inputData || inputData.kind !== 'continuous') return null
    return inputData.input.data
  }, [inputData])

  const sampleCount = continuousData?.length ?? 0

  const canRun =
    continuousData !== null && sampleCount >= MIN_SAMPLES_GAUSSIAN && !isRunning

  // Are all distribution tests passing?
  const allTestsPass = React.useMemo(
    () => result?.distributionTests.every((t: GaussianTestResult) => t.passed) ?? false,
    [result],
  )

  // Cleanup workers on unmount
  React.useEffect(() => {
    return () => {
      workerRef.current?.terminate()
      sp22WorkerRef.current?.terminate()
      sp90bWorkerRef.current?.terminate()
    }
  }, [])

  // Reset PIT result and inline suite results when main result changes
  React.useEffect(() => {
    setPitResult(null)
    setSp22Result(null)
    setSp90bResult(null)
    sp22WorkerRef.current?.terminate()
    sp90bWorkerRef.current?.terminate()
  }, [result])

  // ---------------------------------------------------------------------------
  // Run tests
  // ---------------------------------------------------------------------------
  const handleRun = React.useCallback(() => {
    if (!continuousData) return

    // Instantly jump to top, then state updates — avoids layout-shift jitter
    window.scrollTo(0, 0)

    setIsRunning(true)
    setResult(null)
    setError(null)
    setProgressPct(0)
    setCompletedTests([])
    setPitResult(null)

    workerRef.current?.terminate()

    const worker = new Worker(
      new URL('@/engine/workers/gaussianWorker.ts', import.meta.url),
      { type: 'module' },
    )
    workerRef.current = worker

    const params: Partial<GaussianParameters> = {
      useEstimated,
      alpha,
      ...(useEstimated
        ? {}
        : {
            mu: parseFloat(knownMu) || 0,
            sigma: parseFloat(knownSigma) || 1,
          }),
    }

    worker.onmessage = (e: MessageEvent) => {
      const msg = e.data as {
        type: string
        testName?: string
        testIndex?: number
        totalTests?: number
        result?: GaussianSuiteResult | GaussianTestResult
        error?: string
      }

      if (msg.type === 'progress' && msg.testName) {
        setCompletedTests((prev) => [...prev, msg.testName!])
        const total = msg.totalTests ?? GAUSSIAN_TEST_NAMES.length
        setProgressPct(Math.round(((msg.testIndex! + 1) / total) * 100))
      } else if (msg.type === 'complete') {
        const r = msg.result as GaussianSuiteResult
        setResult(r)
        if (r) persistResult(r)
        setIsRunning(false)
        setProgressPct(100)
        worker.terminate()
      } else if (msg.type === 'error') {
        setError(msg.error ?? 'Unknown worker error')
        setIsRunning(false)
        worker.terminate()
      }
    }

    worker.onerror = (ev) => {
      setError(ev.message ?? 'Worker error')
      setIsRunning(false)
      worker.terminate()
    }

    const buffer = continuousData.buffer.slice(
      continuousData.byteOffset,
      continuousData.byteOffset + continuousData.byteLength,
    )
    worker.postMessage({ type: 'run', data: buffer, params }, [buffer])
  }, [continuousData, useEstimated, knownMu, knownSigma, alpha, persistResult])

  // ---------------------------------------------------------------------------
  // PIT transform
  // ---------------------------------------------------------------------------
  const handlePIT = React.useCallback(async () => {
    if (!continuousData || !result) return
    setIsPitRunning(true)

    try {
      const { probabilityIntegralTransform } = await import(
        '@/engine/gaussian/pit'
      )
      const mu = result.estimatedMu
      const sigma = result.estimatedSigma
      const transformed = probabilityIntegralTransform(
        continuousData,
        mu,
        sigma,
        pitQuantization,
      )

      let bitLength: number
      if (pitQuantization === '1bit') {
        bitLength = continuousData.length
      } else if (pitQuantization === '8bit') {
        bitLength = continuousData.length * 8
      } else {
        bitLength = continuousData.length * 32
      }

      setPitResult({ data: transformed, bitLength })
      // Reset inline suite results when a new PIT transform is done
      setSp22Result(null)
      setSp90bResult(null)
      sp22WorkerRef.current?.terminate()
      sp90bWorkerRef.current?.terminate()
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'PIT transformation failed',
      )
    } finally {
      setIsPitRunning(false)
    }
  }, [continuousData, result, pitQuantization])

  // ---------------------------------------------------------------------------
  // Run SP 800-22 inline on PIT data
  // ---------------------------------------------------------------------------
  const handleRunSP22 = React.useCallback(() => {
    if (!pitResult || pitQuantization !== '1bit') return
    setSp22Running(true)
    setSp22Progress(0)
    setSp22CompletedTests([])
    setSp22Result(null)

    sp22WorkerRef.current?.terminate()

    const worker = new Worker(
      new URL('@/engine/workers/sp80022Worker.ts', import.meta.url),
      { type: 'module' },
    )
    sp22WorkerRef.current = worker

    worker.onmessage = (e: MessageEvent) => {
      const msg = e.data as {
        type: string
        testName?: string
        testIndex?: number
        totalTests?: number
        result?: SP80022SuiteResult | SP80022TestResult
        error?: string
      }

      if (msg.type === 'progress' && msg.testName) {
        setSp22CompletedTests((prev) => [...prev, msg.testName!])
        const total = msg.totalTests ?? 15
        setSp22Progress(Math.round(((msg.testIndex! + 1) / total) * 100))
      } else if (msg.type === 'complete') {
        const r = msg.result as SP80022SuiteResult
        setSp22Result(r)
        persist.setPitSP22Result(r)
        setSp22Running(false)
        setSp22Progress(100)
        worker.terminate()
      } else if (msg.type === 'error') {
        setError(msg.error ?? 'SP 800-22 worker error')
        setSp22Running(false)
        worker.terminate()
      }
    }

    worker.onerror = (ev) => {
      setError(ev.message ?? 'SP 800-22 worker error')
      setSp22Running(false)
      worker.terminate()
    }

    const buffer = (pitResult.data as Uint8Array).buffer.slice(
      pitResult.data.byteOffset,
      pitResult.data.byteOffset + pitResult.data.byteLength,
    )
    worker.postMessage(
      { type: 'run', data: buffer, bitLength: pitResult.bitLength, params: { alpha: 0.01 } },
      [buffer],
    )
  }, [pitResult, pitQuantization])

  // ---------------------------------------------------------------------------
  // Run SP 800-90B inline on PIT data
  // ---------------------------------------------------------------------------
  const handleRunSP90B = React.useCallback(() => {
    if (!pitResult) return
    setSp90bRunning(true)
    setSp90bProgress(0)
    setSp90bProgressMsg('')
    setSp90bResult(null)

    sp90bWorkerRef.current?.terminate()

    const worker = new Worker(
      new URL('@/engine/workers/sp80090bWorker.ts', import.meta.url),
      { type: 'module' },
    )
    sp90bWorkerRef.current = worker

    worker.onmessage = (e: MessageEvent) => {
      const msg = e.data as {
        type: string
        message?: string
        progress?: number
        result?: SP80090BResult
        error?: string
      }

      if (msg.type === 'progress') {
        setSp90bProgress(Math.round((msg.progress ?? 0) * 100))
        setSp90bProgressMsg(msg.message ?? '')
      } else if (msg.type === 'complete') {
        const r = msg.result as SP80090BResult
        setSp90bResult(r)
        persist.setPitSP90BResult(r)
        setSp90bRunning(false)
        setSp90bProgress(100)
        worker.terminate()
      } else if (msg.type === 'error') {
        setError(msg.error ?? 'SP 800-90B worker error')
        setSp90bRunning(false)
        worker.terminate()
      }
    }

    worker.onerror = (ev) => {
      setError(ev.message ?? 'SP 800-90B worker error')
      setSp90bRunning(false)
      worker.terminate()
    }

    // Determine symbol size and prepare data
    let symbolSize: number
    let samples: Uint8Array

    if (pitQuantization === '1bit') {
      // Unpack bits to one-byte-per-sample for SP 800-90B
      symbolSize = 1
      const packed = pitResult.data as Uint8Array
      const numBits = pitResult.bitLength
      samples = new Uint8Array(numBits)
      for (let i = 0; i < numBits; i++) {
        samples[i] = (packed[i >> 3] >> (7 - (i & 7))) & 1
      }
    } else {
      // 8-bit: data is already Uint8Array with one byte per sample
      symbolSize = 8
      samples = pitResult.data as Uint8Array
    }

    const buffer = samples.buffer.slice(
      samples.byteOffset,
      samples.byteOffset + samples.byteLength,
    )
    worker.postMessage(
      { type: 'run', data: buffer, symbolSize, params: { numPermutations: 1000 } },
      [buffer],
    )
  }, [pitResult, pitQuantization])

  // ---------------------------------------------------------------------------
  // Export report
  // ---------------------------------------------------------------------------
  const exportReport = React.useMemo<ExportReport | null>(() => {
    if (!result || !inputData) return null
    const info =
      inputData.kind === 'continuous'
        ? inputData.input
        : inputData.kind === 'binary'
          ? inputData.input
          : inputData.input
    return {
      version: APP_VERSION,
      timestamp: new Date().toISOString(),
      inputFile: {
        name: info.fileName,
        size: info.fileSize,
        sha256: info.sha256 ?? '',
        sampleCount: info.sampleCount,
        format: info.format,
      },
      results: {
        gaussian: result,
      },
    }
  }, [result, inputData])

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-hidden">
      {/* Header */}
      <h1 className="text-2xl font-bold mb-2">
        Gaussian Distribution Testing
      </h1>
      <p className="text-muted-foreground mb-8">
        Distribution validation tests and probability integral transform for
        continuous-valued RNGs.
      </p>

      <div className="grid lg:grid-cols-[380px_1fr] gap-8">
        {/* ================================================================ */}
        {/* LEFT PANEL — Input & Parameters                                  */}
        {/* ================================================================ */}
        <div className="space-y-6 min-w-0">
          <SectionHeader title="Input" icon={FileText} />

          {/* File upload */}
          <FileUploader
            acceptedKinds={['continuous']}
            formatHints="continuous"
            kindMismatchMessage="This module requires continuous float data. For binary data, use SP 800-22 or Tunable-Bias."
            moduleData={inputData}
            onModuleClear={() => { setInputDataLocal(null); persist.clearModuleInput('gaussian') }}
            onDataLoaded={(r) => { setInputDataLocal(r); persist.setModuleInput('gaussian', r); setLocalResult(null) }}
          />

          {/* Import from other modules */}
          {!inputData && (
            <ImportDataBanner
              currentModule="gaussian"
              acceptedKinds={['continuous']}
              onImport={setInputData}
            />
          )}

          {/* OR separator + Data Generator */}
          <div className="relative flex items-center justify-center">
            <Separator className="flex-1" />
            <span className="px-3 text-xs font-medium text-muted-foreground uppercase">
              or
            </span>
            <Separator className="flex-1" />
          </div>

          <DataGenerator mode="continuous" onDataGenerated={handleGeneratedData} />

          <SectionHeader title="Parameters" icon={Sliders} />

          {/* Parameters */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Parameters</CardTitle>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={restoreDefaults}>
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Defaults
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Use estimated toggle */}
              <div className="flex items-center justify-between">
                <Label htmlFor="useEstimated" className="cursor-pointer flex items-center gap-1.5">
                  Use estimated {'\u03BC'}, {'\u03C3'}
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground/50 cursor-help">
                          <Info className="h-3 w-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-[240px]">
                        <p>When enabled, {'\u03BC'} and {'\u03C3'} are estimated from the data using sample mean and standard deviation. Disable to specify known theoretical values from your device model.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Switch
                  id="useEstimated"
                  checked={useEstimated}
                  onCheckedChange={setUseEstimated}
                />
              </div>

              {/* Known mu/sigma inputs (only when not using estimated) */}
              {!useEstimated && (
                <div className="space-y-3 rounded-md bg-muted/50 p-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="knownMu" className="text-xs flex items-center gap-1.5">
                      Known {'\u03BC'} (mean)
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-muted-foreground/50 cursor-help">
                              <Info className="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-[240px]">
                            <p>Theoretical mean of the Gaussian distribution. Use this when you know the expected mean from your device physics or calibration data.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <Input
                      id="knownMu"
                      type="number"
                      step="any"
                      value={knownMu}
                      onChange={(e) => setKnownMu(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="knownSigma" className="text-xs flex items-center gap-1.5">
                      Known {'\u03C3'} (std dev)
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-muted-foreground/50 cursor-help">
                              <Info className="h-3 w-3" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="max-w-[240px]">
                            <p>Theoretical standard deviation of the Gaussian distribution. Must be positive. Use when known from device specifications.</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </Label>
                    <Input
                      id="knownSigma"
                      type="number"
                      step="any"
                      min="0"
                      value={knownSigma}
                      onChange={(e) => setKnownSigma(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Significance level */}
              <div className="space-y-2">
                <Label htmlFor="alpha" className="flex items-center gap-1.5">
                  Significance level {'\u03B1'}
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-muted-foreground/50 cursor-help">
                          <Info className="h-3 w-3" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-[240px]">
                        <p>Threshold for rejecting the null hypothesis of normality. At {'\u03B1'} = 0.01, a test with p-value {'<'} 0.01 is considered a failure. Lower {'\u03B1'} means fewer false rejections but less sensitivity.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
                <Select
                  value={String(alpha)}
                  onValueChange={(v) => setAlpha(Number(v))}
                >
                  <SelectTrigger id="alpha">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.001">0.001</SelectItem>
                    <SelectItem value="0.01">0.01 (default)</SelectItem>
                    <SelectItem value="0.05">0.05</SelectItem>
                    <SelectItem value="0.1">0.10</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Run button */}
              <Button
                className="w-full"
                size="lg"
                disabled={!canRun}
                onClick={handleRun}
                data-testid="run-tests-btn"
              >
                {isRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Run Tests
                  </>
                )}
              </Button>

            </CardContent>
          </Card>

          {/* Info note */}
          <div className="flex gap-2 rounded-md bg-primary/5 p-3">
            <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Upload continuous-valued data (one float per line).
              CSV, TSV, or space-separated formats accepted. Minimum{' '}
              {MIN_SAMPLES_GAUSSIAN} samples.
            </p>
          </div>
        </div>

        {/* ================================================================ */}
        {/* RIGHT PANEL — Results                                            */}
        {/* ================================================================ */}
        <div className="space-y-6 min-w-0">
          <SectionHeader
            title="Results"
            icon={TrendingUp}
            right={
              result ? (
                <>
                  {exportReport && <ExportButton report={exportReport} />}
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCloseResults}>
                    <X className="h-4 w-4" />
                  </Button>
                </>
              ) : undefined
            }
          />

          {/* Progress */}
          {isRunning && (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Test Progress</CardTitle>
                  <span className="text-sm text-muted-foreground">
                    {progressPct}%
                  </span>
                </div>
                <Progress value={progressPct} className="mt-2" />
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {GAUSSIAN_TEST_NAMES.map((name) => {
                    const completed = completedTests.includes(name)
                    const isCurrent =
                      !completed &&
                      completedTests.length < GAUSSIAN_TEST_NAMES.length &&
                      GAUSSIAN_TEST_NAMES.indexOf(name) ===
                        completedTests.length
                    return (
                      <li
                        key={name}
                        className={cn(
                          'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
                          isCurrent && 'bg-primary/5',
                          !completed && !isCurrent && 'text-muted-foreground',
                        )}
                      >
                        {completed ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : isCurrent ? (
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        ) : (
                          <div className="h-4 w-4 rounded-full border border-muted-foreground/30" />
                        )}
                        <span>{name}</span>
                      </li>
                    )
                  })}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Error */}
          {error && (
            <Card className="border-destructive/50">
              <CardContent className="p-6">
                <p className="text-sm text-destructive">{error}</p>
              </CardContent>
            </Card>
          )}

          {/* No results placeholder */}
          {!result && !isRunning && !error && (
            <Card className="border-dashed">
              <CardContent className="p-12">
                <div className="flex flex-col items-center gap-4 text-center">
                  <div className="rounded-full bg-primary/10 p-4">
                    <TrendingUp className="h-8 w-8 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">Ready for Testing</h3>
                    <p className="text-sm text-muted-foreground max-w-md">
                      Upload continuous-valued data and click &ldquo;Run Tests&rdquo; to validate Gaussian distribution
                      and optionally bridge to binary tests via PIT.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2 w-full max-w-lg">
                    <div className="rounded-lg border bg-card/50 p-3 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="h-3.5 w-3.5 text-blue-500" />
                        <p className="text-xs font-medium">Distribution</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground">Shapiro-Wilk, Anderson-Darling, KS, D&apos;Agostino</p>
                    </div>
                    <div className="rounded-lg border bg-card/50 p-3 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                        <p className="text-xs font-medium">Q-Q Plot</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground">Visual diagnostic with reference line</p>
                    </div>
                    <div className="rounded-lg border bg-card/50 p-3 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowRight className="h-3.5 w-3.5 text-orange-500" />
                        <p className="text-xs font-medium">PIT Bridge</p>
                      </div>
                      <p className="text-[10px] text-muted-foreground">Transform to binary for SP 800-22 / 90B</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ============================================================= */}
          {/* RESULTS                                                        */}
          {/* ============================================================= */}
          {result && (
            <>
              {/* Summary bar */}
              <ResultSummaryBar
                passCount={result.distributionTests.filter((t: GaussianTestResult) => t.passed).length}
                failCount={result.distributionTests.filter((t: GaussianTestResult) => !t.passed).length}
                totalTests={result.distributionTests.length}
                overallPassed={result.distributionTests.every((t: GaussianTestResult) => t.passed)}
                executionTimeMs={result.executionTimeMs}
                passMessage="Data consistent with Gaussian distribution"
                failMessage="Data deviates from Gaussian distribution"
                detail={`\u03B1 = ${result.parameters.alpha}`}
              />

              <SnapshotManager module="gaussian" hasResult={!!result} onLoadSnapshot={(snap) => {
                setLocalResult(snap.result)
              }} />

              {/* P-Value chart */}
              <GaussianPValueChart tests={result.distributionTests} alpha={result.parameters.alpha} />

              {/* Distribution test results */}
              <DistributionTestsSection
                tests={result.distributionTests}
                alpha={result.parameters.alpha}
              />

              {/* Parameter estimates */}
              <ParameterEstimatesSection
                mu={result.estimatedMu}
                sigma={result.estimatedSigma}
                muCI={result.muCI}
                sigmaCI={result.sigmaCI}
              />

              {/* Q-Q Plot */}
              <QQPlotSection data={result.qqPlotData} />

              {/* PIT Bridge */}
              <PITBridgeSection
                allTestsPass={allTestsPass}
                quantization={pitQuantization}
                onQuantizationChange={setPitQuantization}
                onTransform={() => void handlePIT()}
                isRunning={isPitRunning}
                pitResult={pitResult}
                sp22Running={sp22Running}
                sp22Progress={sp22Progress}
                sp22CompletedTests={sp22CompletedTests}
                sp22Result={sp22Result}
                onRunSP22={handleRunSP22}
                sp90bRunning={sp90bRunning}
                sp90bProgress={sp90bProgress}
                sp90bProgressMsg={sp90bProgressMsg}
                sp90bResult={sp90bResult}
                onRunSP90B={handleRunSP90B}
              />

              {/* Execution time */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    Total execution time:{' '}
                    <span className="font-mono font-medium text-foreground">
                      {formatTime(result.executionTimeMs)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ===========================================================================
// Gaussian P-Value Summary Chart
// ===========================================================================

const GAUSSIAN_SHORT_NAMES: Record<string, string> = {
  'Shapiro-Wilk': 'S-W',
  'Anderson-Darling': 'A-D',
  "D'Agostino-Pearson": 'D-P',
  'Kolmogorov-Smirnov': 'K-S',
}

interface GaussianPValueChartProps {
  tests: GaussianTestResult[]
  alpha: number
}

function GaussianPValueChart({ tests, alpha }: GaussianPValueChartProps) {
  const chartRef = React.useRef<HTMLDivElement>(null)

  const isDark = document.documentElement.classList.contains('dark')
  const colors = {
    muted: isDark ? '#a1a1aa' : '#71717a',
    border: isDark ? '#3f3f46' : '#e4e4e7',
  }

  const plotData = tests.map((t, i) => ({
    x: i,
    name: GAUSSIAN_SHORT_NAMES[t.testName] ?? t.testName.substring(0, 4),
    fullName: t.testName,
    pValue: Math.max(t.pValue, 1e-10),
    passed: t.passed,
  }))

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.[0]) return null
    const d = payload[0].payload
    return (
      <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
        <p className="font-medium">{d.fullName}</p>
        <p className="font-mono mt-0.5">
          p = {d.pValue < 0.0001 ? d.pValue.toExponential(3) : d.pValue.toFixed(6)}
        </p>
        <p className={d.passed ? 'text-green-500' : 'text-red-500'}>
          {d.passed ? 'PASS' : 'FAIL'}
        </p>
      </div>
    )
  }

  const yTicks = [1, 0.1, 0.01, 0.001, 0.0001]

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            P-Value Summary <span className="text-[10px] font-normal text-muted-foreground ml-1">(log scale)</span>
          </CardTitle>
          <ChartExportButton containerRef={chartRef} fileName="gaussian-pvalues" />
        </div>
      </CardHeader>
      <CardContent className="pb-4 px-2 sm:px-4">
        <div ref={chartRef} role="img" aria-label="P-value summary chart for Gaussian distribution tests on a logarithmic scale">
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 12, right: 12, bottom: 50, left: 4 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={colors.border}
                  opacity={0.5}
                  horizontal
                  vertical={false}
                />
                <XAxis
                  dataKey="x"
                  type="number"
                  domain={[-0.5, plotData.length - 0.5]}
                  ticks={plotData.map((_, i) => i)}
                  tickFormatter={(i: number) => plotData[i]?.name ?? ''}
                  tick={{ fontSize: 9, fill: colors.muted }}
                  tickLine={false}
                  axisLine={{ stroke: colors.border }}
                  angle={-40}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  dataKey="pValue"
                  type="number"
                  scale="log"
                  domain={[
                    Math.min(0.0001, ...plotData.map(d => d.pValue)) / 2,
                    1,
                  ]}
                  ticks={yTicks}
                  tickFormatter={(v: number) => v >= 0.01 ? v.toString() : v.toExponential(0)}
                  tick={{ fontSize: 9, fill: colors.muted }}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                />
                <ReferenceLine
                  y={alpha}
                  stroke="#ef4444"
                  strokeDasharray="6 3"
                  strokeWidth={2}
                  label={{
                    value: `\u03B1 = ${alpha}`,
                    position: 'insideBottomLeft',
                    style: { fontSize: 9, fill: '#ef4444', fontWeight: 600 },
                    offset: 4,
                  }}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Scatter data={plotData} isAnimationActive={false}>
                  {plotData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.passed ? '#22c55e' : '#ef4444'}
                      r={8}
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1 px-2">
          Each dot is one test's p-value (log scale). Dots below the {'\u03B1'} = {alpha} line indicate failure.
        </p>
      </CardContent>
    </Card>
  )
}

// ===========================================================================
// Distribution Tests Section
// ===========================================================================

interface DistributionTestsSectionProps {
  tests: GaussianTestResult[]
  alpha: number
}

function DistributionTestsSection({
  tests,
  alpha,
}: DistributionTestsSectionProps) {
  const passCount = tests.filter((t) => t.passed).length
  const allPass = passCount === tests.length

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Distribution Tests
            <DataExportMenu
              data={tests.map((t) => {
                const stat = getTestStatistic(t)
                return {
                  testName: t.testName,
                  statistic: stat.value,
                  statisticLabel: stat.label,
                  pValue: t.pValue,
                  passed: t.passed,
                }
              })}
              fileName="gaussian-distribution-tests"
              columns={['testName', 'statistic', 'pValue', 'passed']}
              title="Gaussian Distribution Test Results"
            />
          </CardTitle>
          <Badge
            variant={allPass ? 'success' : 'destructive'}
            className="text-sm px-3 py-1"
          >
            {allPass ? 'Gaussian' : 'Non-Gaussian'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Significance level {'\u03B1'} = {alpha}. Null hypothesis H
          {'\u2080'}: data follows a Gaussian distribution.
        </p>

        <div className="space-y-3">
          {tests.map((test, idx) => {
            const stat = getTestStatistic(test)
            return (
              <div
                key={idx}
                className={cn(
                  'rounded-lg border p-4',
                  test.passed
                    ? 'border-border'
                    : 'border-red-500/30 bg-red-500/5',
                )}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {test.passed ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span className="font-medium text-sm">
                      {test.testName}
                    </span>
                  </div>
                  <Badge variant={test.passed ? 'success' : 'destructive'}>
                    {test.passed ? 'Pass' : 'Fail'}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Statistic ({stat.label})
                    </p>
                    <p className="font-mono text-sm">
                      {stat.value.toFixed(6)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">p-value</p>
                    <p
                      className={cn(
                        'font-mono text-sm',
                        pValueColor(test.pValue),
                      )}
                    >
                      {formatPValue(test.pValue)}
                    </p>
                  </div>
                </div>

                {/* Extra details for D'Agostino-Pearson */}
                {test.testName === "D'Agostino-Pearson" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 pt-2 border-t border-border/50">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        Skewness Z
                      </p>
                      <p className="font-mono text-xs">
                        {(test as DAgostinoPearsonResult).skewnessZ.toFixed(4)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">
                        Kurtosis Z
                      </p>
                      <p className="font-mono text-xs">
                        {(test as DAgostinoPearsonResult).kurtosisZ.toFixed(4)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Extra details for Anderson-Darling */}
                {test.testName === 'Anderson-Darling' && (
                  <div className="mt-2 pt-2 border-t border-border/50">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        A{'\u00B2'}* (adjusted)
                      </p>
                      <p className="font-mono text-xs">
                        {(test as AndersonDarlingResult).A2star.toFixed(6)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

// ===========================================================================
// Parameter Estimates Section
// ===========================================================================

interface ParameterEstimatesSectionProps {
  mu: number
  sigma: number
  muCI: [number, number]
  sigmaCI: [number, number]
}

function ParameterEstimatesSection({
  mu,
  sigma,
  muCI,
  sigmaCI,
}: ParameterEstimatesSectionProps) {
  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
            <CardTitle className="text-base">Parameter Estimates</CardTitle>
            <DataExportMenu
              data={[{
                parameter: 'mu',
                estimate: mu,
                ciLow: muCI[0],
                ciHigh: muCI[1],
              }, {
                parameter: 'sigma',
                estimate: sigma,
                ciLow: sigmaCI[0],
                ciHigh: sigmaCI[1],
              }]}
              fileName="gaussian-parameter-estimates"
              columns={['parameter', 'estimate', 'ciLow', 'ciHigh']}
              title="Parameter Estimates"
            />
          </div>
      </CardHeader>
      <CardContent>
        <div className="grid sm:grid-cols-2 gap-6">
          {/* Mu */}
          <div className="rounded-lg bg-muted/50 p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">
              Mean ({'\u03BC'})
            </p>
            <p className="text-2xl font-bold font-mono">{mu.toPrecision(6)}</p>
            <p className="text-xs text-muted-foreground mt-2">
              95% CI: [{muCI[0].toPrecision(6)}, {muCI[1].toPrecision(6)}]
            </p>
          </div>

          {/* Sigma */}
          <div className="rounded-lg bg-muted/50 p-4 text-center">
            <p className="text-xs text-muted-foreground mb-1">
              Std Dev ({'\u03C3'})
            </p>
            <p className="text-2xl font-bold font-mono">
              {sigma.toPrecision(6)}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              95% CI: [{sigmaCI[0].toPrecision(6)},{' '}
              {sigmaCI[1].toPrecision(6)}]
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ===========================================================================
// Q-Q Plot Section
// ===========================================================================

interface QQPlotSectionProps {
  data: Array<{ theoretical: number; observed: number }>
}

function QQPlotSection({ data }: QQPlotSectionProps) {
  const qqChartRef = React.useRef<HTMLDivElement>(null)
  const plotData = React.useMemo(() => subsampleQQ(data, 1000), [data])

  // Compute axis range from the data
  const { minVal, maxVal } = React.useMemo(() => {
    let min = Infinity
    let max = -Infinity
    for (const pt of plotData) {
      const lo = Math.min(pt.theoretical, pt.observed)
      const hi = Math.max(pt.theoretical, pt.observed)
      if (lo < min) min = lo
      if (hi > max) max = hi
    }
    // Add padding
    const pad = (max - min) * 0.05
    return { minVal: min - pad, maxVal: max + pad }
  }, [plotData])

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
            <CardTitle className="text-base">Q-Q Plot</CardTitle>
            <div className="flex items-center gap-1">
              <ChartExportButton containerRef={qqChartRef} fileName="qq-plot" />
              <DataExportMenu
                data={data.map((d) => ({
                  theoretical: d.theoretical,
                  observed: d.observed,
                }))}
                fileName="qq-plot-data"
                columns={['theoretical', 'observed']}
                title="Q-Q Plot Data"
              />
            </div>
          </div>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4">
          Theoretical quantiles (x-axis) vs observed quantiles (y-axis).
          Points along the y = x line indicate a good Gaussian fit.
          {data.length > 1000 && (
            <span className="ml-1">
              Subsampled to 1,000 points for rendering performance.
            </span>
          )}
        </p>
        <div ref={qqChartRef} role="img" aria-label="Q-Q plot comparing theoretical vs observed quantiles for Gaussian fit assessment">
          <div className="h-[400px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 20, bottom: 40, left: 20 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  className="stroke-border"
                />
                <XAxis
                  type="number"
                  dataKey="theoretical"
                  name="Theoretical"
                  domain={[minVal, maxVal]}
                  tickFormatter={(v: number) => v.toFixed(1)}
                  label={{
                    value: 'Theoretical Quantiles',
                    position: 'insideBottom',
                    offset: -25,
                    className: 'fill-muted-foreground text-xs',
                  }}
                  className="text-xs"
                />
                <YAxis
                  type="number"
                  dataKey="observed"
                  name="Observed"
                  domain={[minVal, maxVal]}
                  tickFormatter={(v: number) => v.toFixed(1)}
                  label={{
                    value: 'Observed Quantiles',
                    angle: -90,
                    position: 'insideLeft',
                    offset: 0,
                    className: 'fill-muted-foreground text-xs',
                  }}
                  className="text-xs"
                />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (!active || !payload || payload.length < 1) return null
                    const item = payload[0]?.payload as {
                      theoretical: number
                      observed: number
                    }
                    if (!item) return null
                    return (
                      <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
                        <p>
                          <span className="text-muted-foreground">
                            Theoretical:{' '}
                          </span>
                          <span className="font-mono">
                            {item.theoretical.toFixed(4)}
                          </span>
                        </p>
                        <p>
                          <span className="text-muted-foreground">
                            Observed:{' '}
                          </span>
                          <span className="font-mono">
                            {item.observed.toFixed(4)}
                          </span>
                        </p>
                      </div>
                    )
                  }}
                />
                {/* y = x reference line */}
                <ReferenceLine
                  segment={[
                    { x: minVal, y: minVal },
                    { x: maxVal, y: maxVal },
                  ]}
                  strokeDasharray="6 4"
                  className="stroke-primary"
                  strokeWidth={1.5}
                />
                <Scatter
                  data={plotData}
                  fill="hsl(var(--primary))"
                  fillOpacity={0.5}
                  r={2}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ===========================================================================
// PIT Bridge Section (Enhanced — inline SP 800-22 & SP 800-90B)
// ===========================================================================

interface PITBridgeSectionProps {
  allTestsPass: boolean
  quantization: '1bit' | '8bit' | '32bit'
  onQuantizationChange: (q: '1bit' | '8bit' | '32bit') => void
  onTransform: () => void
  isRunning: boolean
  pitResult: { data: Uint8Array | Uint32Array; bitLength: number } | null
  sp22Running: boolean
  sp22Progress: number
  sp22CompletedTests: string[]
  sp22Result: SP80022SuiteResult | null
  onRunSP22: () => void
  sp90bRunning: boolean
  sp90bProgress: number
  sp90bProgressMsg: string
  sp90bResult: SP80090BResult | null
  onRunSP90B: () => void
}

function PITBridgeSection({
  allTestsPass,
  quantization,
  onQuantizationChange,
  onTransform,
  isRunning,
  pitResult,
  sp22Running,
  sp22Progress,
  sp22CompletedTests,
  sp22Result,
  onRunSP22,
  sp90bRunning,
  sp90bProgress,
  sp90bProgressMsg,
  sp90bResult,
  onRunSP90B,
}: PITBridgeSectionProps) {
  const canRunSP22 = pitResult !== null && quantization === '1bit' && !sp22Running
  const canRunSP90B = pitResult !== null && quantization !== '32bit' && !sp90bRunning
  const isExportOnly = quantization === '32bit'

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ArrowRight className="h-4 w-4" />
            PIT Bridge
          </CardTitle>
          {allTestsPass ? (
            <Badge variant="success">Ready</Badge>
          ) : (
            <Badge variant="warning">Tests not all passing</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Apply the Probability Integral Transform (PIT) to convert Gaussian
          data into uniform bits, then run SP 800-22 or SP 800-90B tests inline.
          U{'\u1D62'} = {'\u03A6'}((X{'\u1D62'} - {'\u03BC'}) / {'\u03C3'})
        </p>

        {!allTestsPass && (
          <div className="flex gap-2 rounded-md bg-yellow-500/10 p-3">
            <Info className="h-4 w-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-600 dark:text-yellow-400">
              Not all distribution tests passed. PIT results may not be
              meaningful if data is not truly Gaussian.
            </p>
          </div>
        )}

        <div className="flex gap-2 rounded-md bg-muted/50 p-3">
          <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-[10px] text-muted-foreground">
            <strong>Note for MTJ p-bit data:</strong> PIT is designed for continuous-valued sources.
            For intrinsically binary MTJ output, 1-bit quantization is equivalent to debiasing,
            destroying the bias information. Use the Tunable-Bias module directly instead.
          </p>
        </div>

        <div className="flex items-end gap-3">
          <div className="space-y-2 flex-1">
            <Label htmlFor="pitQuant" className="flex items-center gap-1.5">
              Quantization
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground/50 cursor-help">
                      <Info className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[240px]">
                    <p>Determines how the uniform PIT output U_i {'\u2208'} [0,1) is discretized. 1-bit: threshold at 0.5 {'\u2192'} binary for SP 800-22. 8-bit: multiply by 256 {'\u2192'} byte symbols for SP 800-90B. 32-bit: maximum precision for external analysis.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </Label>
            <Select
              value={quantization}
              onValueChange={(v) =>
                onQuantizationChange(v as '1bit' | '8bit' | '32bit')
              }
            >
              <SelectTrigger id="pitQuant">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1bit">1-bit — SP 800-22 + SP 800-90B</SelectItem>
                <SelectItem value="8bit">8-bit — SP 800-90B only</SelectItem>
                <SelectItem value="32bit">32-bit — export for external tools</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={onTransform} disabled={isRunning}>
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Transforming...
              </>
            ) : (
              <>
                <ArrowRight className="h-4 w-4" />
                Transform
              </>
            )}
          </Button>
        </div>

        {pitResult && (
          <>
            {/* Transform result summary */}
            <div className="rounded-lg bg-green-500/5 border border-green-500/20 p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">
                  PIT transformation complete
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Output bits</p>
                  <p className="font-mono">
                    {pitResult.bitLength.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Output size</p>
                  <p className="font-mono">
                    {(pitResult.data.byteLength / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
            </div>

            {/* 32-bit export-only note */}
            {isExportOnly && (
              <div className="flex gap-2 rounded-md bg-muted/50 p-3">
                <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  32-bit quantization preserves maximum precision (U<sub>i</sub> × 2<sup>32</sup> → uint32) but exceeds the symbol size supported by in-app tests. Use the export button below to download the transformed data for analysis in external tools such as Origin, Python, or MATLAB.
                </p>
              </div>
            )}

            {/* Run buttons */}
            {!isExportOnly && <div className="flex gap-3">
              {quantization === '1bit' && (
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={!canRunSP22}
                  onClick={onRunSP22}
                >
                  {sp22Running ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Running SP 800-22...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4" />
                      Run SP 800-22
                    </>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                className="flex-1"
                disabled={!canRunSP90B}
                onClick={onRunSP90B}
              >
                {sp90bRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running SP 800-90B...
                  </>
                ) : (
                  <>
                    <Activity className="h-4 w-4" />
                    Run SP 800-90B
                  </>
                )}
              </Button>
            </div>}

            {/* SP 800-22 progress */}
            {sp22Running && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">SP 800-22 Progress</span>
                  <span className="font-mono">{sp22Progress}%</span>
                </div>
                <Progress value={sp22Progress} />
                {sp22CompletedTests.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Completed: {sp22CompletedTests[sp22CompletedTests.length - 1]}
                  </p>
                )}
              </div>
            )}

            {/* SP 800-22 results — full detail matching standalone page */}
            {sp22Result && (
              <InlineSP22Results
                suiteResult={sp22Result}
                pitResult={pitResult}
              />
            )}

            {/* SP 800-90B progress */}
            {sp90bRunning && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">SP 800-90B Progress</span>
                  <span className="font-mono">{sp90bProgress}%</span>
                </div>
                <Progress value={sp90bProgress} />
                {sp90bProgressMsg && (
                  <p className="text-xs text-muted-foreground">{sp90bProgressMsg}</p>
                )}
              </div>
            )}

            {/* SP 800-90B results — full detail matching standalone page */}
            {sp90bResult && (
              <InlineSP90BResults
                result={sp90bResult}
                pitResult={pitResult}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ===========================================================================
// Inline SP 800-22 Results — full detail matching NIST80022Page
// ===========================================================================

interface InlineSP22ResultsProps {
  suiteResult: SP80022SuiteResult
  pitResult: { data: Uint8Array | Uint32Array; bitLength: number } | null
}

function InlineSP22Results({ suiteResult, pitResult }: InlineSP22ResultsProps) {
  const { passCount, failCount, totalTests, executionTimeMs, overallPassed } = suiteResult
  const alpha = suiteResult.parameters.alpha
  const passPercent = totalTests > 0 ? (passCount / totalTests) * 100 : 0

  const exportReport = React.useMemo<ExportReport | null>(() => {
    if (!pitResult) return null
    return {
      version: APP_VERSION,
      timestamp: new Date().toISOString(),
      inputFile: {
        name: 'pit-transformed',
        size: pitResult.data.byteLength,
        sha256: '',
        sampleCount: pitResult.bitLength,
        format: 'binary' as InputFormat,
      },
      results: { sp80022: suiteResult },
    }
  }, [suiteResult, pitResult])

  return (
    <div className="space-y-4">
      {/* Summary bar — matches NIST80022Page SummaryBar */}
      <Card className={cn(
        'border-2',
        overallPassed
          ? 'border-green-500/30 bg-green-500/5 dark:border-green-500/20 dark:bg-green-500/5'
          : 'border-red-500/30 bg-red-500/5 dark:border-red-500/20 dark:bg-red-500/5'
      )}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className={cn(
                'flex items-center justify-center h-12 w-12 rounded-full',
                overallPassed
                  ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                  : 'bg-red-500/15 text-red-600 dark:text-red-400'
              )}>
                {overallPassed ? (
                  <Shield className="h-6 w-6" />
                ) : (
                  <AlertCircle className="h-6 w-6" />
                )}
              </div>
              <div>
                <p className="text-lg font-semibold">
                  {passCount}/{totalTests} Tests Passed
                </p>
                <p className="text-sm text-muted-foreground">
                  {overallPassed ? 'Sequence appears random' : 'Sequence may not be random'}
                  {' '}&middot;{' '}
                  <span className="font-mono">{'\u03B1'} = {alpha}</span>
                  {' '}&middot;{' '}
                  <span className="font-mono">{formatTime(executionTimeMs)}</span>
                </p>
              </div>
            </div>
            <div className="w-full sm:w-48">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>{passCount} pass</span>
                <span>{failCount} fail</span>
              </div>
              <div className="h-2.5 rounded-full bg-muted overflow-hidden flex">
                <div
                  className="h-full rounded-l-full transition-all bg-green-500"
                  style={{ width: `${passPercent}%` }}
                />
                {failCount > 0 && (
                  <div
                    className="h-full bg-red-500 rounded-r-full"
                    style={{ width: `${100 - passPercent}%` }}
                  />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export row */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Detailed results for each of the 15 tests:
        </p>
        <div className="flex items-center gap-1">
          {exportReport && <ExportButton report={exportReport} />}
        </div>
      </div>

      {/* Individual test result cards — matches NIST80022Page InlineResultCard */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {suiteResult.tests.map((result) => (
          <PITSP22ResultCard
            key={result.testName}
            result={result}
            alpha={alpha}
          />
        ))}
      </div>
    </div>
  )
}

// Single SP 800-22 result card — matches NIST80022Page's InlineResultCard exactly
function PITSP22ResultCard({ result, alpha }: { result: SP80022TestResult; alpha: number }) {
  const [expanded, setExpanded] = React.useState(false)
  const isMultiPValue = result.pValues.length > 1
  const passCount = result.passed.filter(Boolean).length
  const failCount = result.passed.length - passCount

  const representativePValue = isMultiPValue
    ? Math.min(...result.pValues)
    : result.pValues[0] ?? 0

  return (
    <Card className={cn(
      'transition-all',
      result.overallPassed
        ? 'border-green-500/20 dark:border-green-500/15'
        : 'border-red-500/30 dark:border-red-500/20'
    )}>
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {result.overallPassed ? (
              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
            ) : (
              <XCircle className="h-4 w-4 text-red-500 shrink-0" />
            )}
            <CardTitle className="text-sm font-medium truncate">
              {result.testName}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isMultiPValue && (
              <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
                {passCount}/{result.pValues.length}
              </Badge>
            )}
            <Badge variant={result.overallPassed ? 'success' : 'destructive'} className="text-[10px] px-2 py-0">
              {result.overallPassed ? 'PASS' : 'FAIL'}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Statistic</p>
            <p className="font-mono text-xs">{result.statistic.toFixed(6)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
              {isMultiPValue ? 'Min p-value' : 'p-value'}
            </p>
            <p className={cn('font-mono text-xs', sp22PValueColor(representativePValue, alpha))}>
              {formatPValue(representativePValue)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Time</p>
            <p className="font-mono text-xs">{result.executionTimeMs.toFixed(0)}ms</p>
          </div>
        </div>

        {/* Multi-p-value detail expansion */}
        {isMultiPValue && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronDown
                className={cn(
                  'h-3 w-3 transition-transform',
                  expanded && 'rotate-180'
                )}
              />
              {expanded
                ? 'Hide sub-results'
                : `Show ${result.pValues.length} sub-results (${passCount} pass, ${failCount} fail)`}
            </button>
            {expanded && (
              <div className="mt-2 max-h-48 overflow-x-auto overflow-y-auto rounded-md border bg-muted/30">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-2 py-1.5 text-left font-medium">#</th>
                      <th className="px-2 py-1.5 text-right font-medium">p-value</th>
                      <th className="px-2 py-1.5 text-center font-medium">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.pValues.map((pv, i) => (
                      <tr
                        key={i}
                        className={cn(
                          'border-b border-muted/50 last:border-0',
                          sp22PValueBgColor(pv, alpha)
                        )}
                      >
                        <td className="px-2 py-1 font-mono text-muted-foreground">{i + 1}</td>
                        <td className={cn('px-2 py-1 text-right font-mono', sp22PValueColor(pv, alpha))}>
                          {formatPValue(pv)}
                        </td>
                        <td className="px-2 py-1 text-center">
                          {result.passed[i] ? (
                            <CheckCircle2 className="h-3 w-3 text-green-500 inline" />
                          ) : (
                            <XCircle className="h-3 w-3 text-red-500 inline" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {result.notes && (
          <p className="mt-2 text-[10px] text-muted-foreground italic">{result.notes}</p>
        )}
      </CardContent>
    </Card>
  )
}

// ===========================================================================
// Inline SP 800-90B Results — full detail matching SP80090BPage
// ===========================================================================

interface InlineSP90BResultsProps {
  result: SP80090BResult
  pitResult: { data: Uint8Array | Uint32Array; bitLength: number } | null
}

function InlineSP90BResults({ result, pitResult }: InlineSP90BResultsProps) {
  const exportReport = React.useMemo<ExportReport | null>(() => {
    if (!pitResult) return null
    return {
      version: APP_VERSION,
      timestamp: new Date().toISOString(),
      inputFile: {
        name: 'pit-transformed',
        size: pitResult.data.byteLength,
        sha256: '',
        sampleCount: pitResult.bitLength,
        format: 'binary' as InputFormat,
      },
      results: { sp80090b: result },
    }
  }, [result, pitResult])

  return (
    <div className="space-y-4">
      {/* IID section */}
      <PITIIDResultsSection
        statistics={result.iidTestResults.statistics}
        conclusion={result.iidTestResults.iidConclusion}
        numPermutations={result.iidTestResults.numPermutations}
      />

      {/* Entropy section */}
      <PITEntropyEstimatesSection
        estimates={result.entropyEstimates}
        finalMinEntropy={result.finalMinEntropy}
        theoreticalMax={result.theoreticalMaxEntropy}
        efficiency={result.entropyEfficiency}
      />

      {/* Execution time & export */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Total execution time:{' '}
              <span className="font-mono font-medium text-foreground">
                {formatTime(result.executionTimeMs)}
              </span>
            </div>
            {exportReport && <ExportButton report={exportReport} />}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// IID Results — matches SP80090BPage's IIDResultsSection exactly
function PITIIDResultsSection({
  statistics,
  conclusion,
  numPermutations,
}: {
  statistics: IIDStatisticResult[]
  conclusion: boolean
  numPermutations: number
}) {
  const passCount = statistics.filter((s) => s.passed).length
  const failCount = statistics.length - passCount

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4" />
            IID Hypothesis Test
            <DataExportMenu
              data={statistics.map((s) => ({
                name: s.name,
                originalValue: s.originalValue,
                rank: s.rank,
                totalPermutations: s.totalPermutations,
                passed: s.passed,
              }))}
              fileName="pit-sp800-90b-iid-statistics"
              columns={['name', 'originalValue', 'rank', 'totalPermutations', 'passed']}
              title="PIT SP 800-90B IID Statistics"
            />
          </CardTitle>
          <Badge
            variant={conclusion ? 'success' : 'destructive'}
            className="text-sm px-3 py-1"
          >
            {conclusion ? 'IID' : 'Non-IID'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary row */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
          <div className="rounded-lg bg-muted/50 p-3">
            <p className="text-xs text-muted-foreground">Tests Run</p>
            <p className="text-lg font-semibold font-mono">
              {statistics.length}
            </p>
          </div>
          <div className="rounded-lg bg-green-500/10 p-3">
            <p className="text-xs text-muted-foreground">Passed</p>
            <p className="text-lg font-semibold font-mono text-green-600 dark:text-green-400">
              {passCount}
            </p>
          </div>
          <div className="rounded-lg bg-red-500/10 p-3">
            <p className="text-xs text-muted-foreground">Failed</p>
            <p className="text-lg font-semibold font-mono text-red-600 dark:text-red-400">
              {failCount}
            </p>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          Permutation test with {numPermutations.toLocaleString()} permutations.
          A statistic fails if its rank is in the top or bottom 5 out of{' '}
          {numPermutations.toLocaleString()}.
        </p>

        <Separator />

        {/* Show failed first, then collapsible passed */}
        <div className="space-y-2">
          {statistics
            .filter((s) => !s.passed)
            .map((stat, idx) => (
              <PITIIDStatisticRow key={`fail-${idx}`} stat={stat} />
            ))}
          {statistics.filter((s) => s.passed).length > 0 && (
            <PITCollapsiblePassedStats
              stats={statistics.filter((s) => s.passed)}
            />
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function PITIIDStatisticRow({ stat }: { stat: IIDStatisticResult }) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm',
        !stat.passed && 'bg-red-500/5',
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        {stat.passed ? (
          <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
        ) : (
          <XCircle className="h-4 w-4 text-red-500 shrink-0" />
        )}
        <span className="truncate">{stat.name}</span>
      </div>
      <div className="flex items-center gap-4 shrink-0 text-xs text-muted-foreground">
        <span className="font-mono">T = {stat.originalValue.toFixed(4)}</span>
        <span className="font-mono">
          rank {stat.rank}/{stat.totalPermutations}
        </span>
        <Badge variant={stat.passed ? 'success' : 'destructive'} className="text-[10px]">
          {stat.passed ? 'Pass' : 'Fail'}
        </Badge>
      </div>
    </div>
  )
}

function PITCollapsiblePassedStats({ stats }: { stats: IIDStatisticResult[] }) {
  const [expanded, setExpanded] = React.useState(false)

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-2"
      >
        <CheckCircle2 className="h-4 w-4 text-green-500" />
        {expanded
          ? `Hide ${stats.length} passed statistics`
          : `Show ${stats.length} passed statistics`}
      </button>
      {expanded &&
        stats.map((stat, idx) => (
          <PITIIDStatisticRow key={`pass-${idx}`} stat={stat} />
        ))}
    </div>
  )
}

// Entropy Estimates — matches SP80090BPage's EntropyEstimatesSection exactly
function PITEntropyEstimatesSection({
  estimates,
  finalMinEntropy,
  theoreticalMax,
  efficiency,
}: {
  estimates: EntropyEstimateResult[]
  finalMinEntropy: number
  theoreticalMax: number
  efficiency: number
}) {
  const efficiencyPct = Math.round(efficiency * 100)

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Entropy Estimation
            <DataExportMenu
              data={estimates.map((e) => ({
                estimator: e.estimatorName,
                minEntropy: e.minEntropy,
              }))}
              fileName="pit-sp800-90b-entropy-estimates"
              columns={['estimator', 'minEntropy']}
              title="PIT SP 800-90B Entropy Estimates"
            />
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Final min-entropy highlight */}
        <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">
            Final Min-Entropy Estimate
          </p>
          <p className="text-3xl font-bold font-mono text-primary">
            {formatEntropy(finalMinEntropy)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            bits per symbol (theoretical max: {theoreticalMax})
          </p>
        </div>

        {/* Entropy efficiency bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Entropy Efficiency</span>
            <span
              className={cn(
                'font-mono font-medium',
                efficiencyPct >= 90
                  ? 'text-green-600 dark:text-green-400'
                  : efficiencyPct >= 70
                    ? 'text-yellow-600 dark:text-yellow-400'
                    : 'text-red-600 dark:text-red-400',
              )}
            >
              {efficiencyPct}%
            </span>
          </div>
          <Progress value={efficiencyPct} />
        </div>

        <Separator />

        {/* Individual estimators */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground mb-2">
            Individual Estimators
          </p>
          {estimates.map((est, idx) => {
            const isMin =
              Math.abs(est.minEntropy - finalMinEntropy) < 1e-9
            return (
              <div
                key={idx}
                className={cn(
                  'flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm',
                  isMin && 'bg-primary/5 ring-1 ring-primary/20',
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div
                    className={cn(
                      'h-2 w-2 rounded-full shrink-0',
                      isMin ? 'bg-primary' : 'bg-muted-foreground/30',
                    )}
                  />
                  <span className="truncate">{est.estimatorName}</span>
                  {isMin && (
                    <Badge variant="default" className="text-[10px]">
                      MIN
                    </Badge>
                  )}
                </div>
                <span className="font-mono text-sm shrink-0">
                  {formatEntropy(est.minEntropy)}
                </span>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
